import React, { FC, useEffect, useRef, useState } from 'react';
import { SearchInputSmall } from '../common/SearchInputSmall';
import { ButtonComponent } from '../ButtonComponent';
import { FilterComponent } from '../FilterComponent';
import { SelectPicker } from 'rsuite';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { AddNewPartner } from './AddNewPartner';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { setPartnerFilters, setPartnerFiltersToDefault } from '../../store/merchnts/actions/partners';
import { useLocation } from 'react-router';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { useNavigate } from 'react-router-dom';

export interface IPartnerFilters {
  type?: string;
  query?: string;
  pageNumber?: string;
  pageSize?: string;
}

interface Props {
  searchValue: string;
  setSearchValue: (value: string) => void;
}

export const PartnerListFilters: FC<Props> = (props) => {
  const { searchValue, setSearchValue } = props;
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const isMounted = useRef(false);

  const filtersFromUrl = parseSearchParams(search) || {};

  const { merchantState: { type, merchantsLists }, dispatch } = useMerchants();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  useEffect(() => {
    if (!isMounted.current) return;

    const params: IPartnerFilters = filtersFromUrl;
    params.type = type;

    navigate(`${pathname}?${makeSearchParams(params)}`);
  }, [type]);

  useEffect(() => {
    if (!isMounted.current) {
      // first mount
      isMounted.current = true;
    }
  }, []);

  useEffect(() => {
    return () => {
      setPartnerFiltersToDefault(dispatch);
    };
  }, []);

  const closeModal = (): void => setModalState(prevState => ({ ...prevState, isOpen: false }));

  const handleSuccess = (): void => {
    setIsModalOpen(false);
    setModalState({
      isOpen: true,
      modalTitle: t('PARTNERS.MODAL_TITLE'),
      // modalTitle: chosenPartner?.id ? t('PARTNERS.MODAL_TITLE_EDIT') : t('PARTNERS.MODAL_TITLE'),
      content: (
        <div className="fs-6">
          {t('PARTNERS.SUCCESS_MODAL_TEXT')}
        </div>
      ),
      confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
      minHeight: 100,
      onConfirm: () => setModalState(prevState => ({ ...prevState, isOpen: false })),
      onClose: closeModal
    });
  };

  const handleError = (): void => {
    setIsModalOpen(false);
    setModalState({
      isOpen: true,
      modalTitle: t('PARTNERS.MODAL_TITLE'),
      // modalTitle: chosenPartner?.id ? t('PARTNERS.MODAL_TITLE_EDIT') : t('PARTNERS.MODAL_TITLE'),
      content: (
        <div className="fs-6">
          {t('PARTNERS.FAIL_MODAL_TEXT')}
        </div>
      ),
      confirmButtonText: t('PARTNERS.FAIL_BTN_NAME'),
      minHeight: 100,
      onConfirm: () => {
        setModalState(prevState => ({ ...prevState, isOpen: false }));
        setIsModalOpen(true);
      },
      onClose: closeModal
    });
  };

  return (
    <>
      <div className="card bg-transparent">
        <div className="card-body px-0 py-0 d-sm-flex justify-content-between align-items-center mb-4">
          <div className="w-md-400px application-selector application-additional-class">

            <SearchInputSmall
              placeholder={t('PARTNERS.SEARCH_PLACEHOLDER')}
              additionalClass=""
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />

          </div>
          <div className="mt-5 mt-sm-0">
            <ButtonComponent
              buttonName={t('PARTNERS.BTN_NAME')}
              onButtonClick={(): void => setIsModalOpen(true)}
              additionalClass="btn-sm w-100 w-sm-120px"
            />
          </div>
        </div>
        <div className="d-flex align-items-center fc-filter-line">
          <FilterComponent value={type} label="Type" labelPlacement="integrated">
            <SelectPicker
              data={merchantsLists?.partnerTypes}
              placeholder='Type'
              onSelect={(value): void => setPartnerFilters({ dispatch, filters: { type: value }})}
              onClean={(): void => setPartnerFilters({ dispatch, filters: { type: '' }})}
              value={type}
              searchable={false}
              defaultValue={type}
              className={clsx('fc-selector timezone-selector', type ? 'fc-selector-cleanable' : 'fc-added-icon')}
              appearance="subtle"
              placement="bottomStart"
            />
          </FilterComponent>
        </div>
      </div>

      <ModalWindow
        {...modalState}
        onClose={closeModal}
      />

      <ModalWindow
        isOpen={isModalOpen}
        backdrop="static"
        onClose={(): void => setIsModalOpen(false)}
        modalTitle={t('PARTNERS.MODAL_TITLE')}
        content={
          <AddNewPartner
            onSuccess={handleSuccess}
            onError={handleError}
          />
        }

      />
    </>
  );
};